<template>
  <div class="relative w-full overflow-hidden rounded-lg border bg-white text-left">
    <div
      class="flex flex-col gap-3 px-4 py-3 md:grid md:grid-cols-12 md:items-center md:gap-6 md:px-6 md:py-4"
    >
      <div class="md:col-span-3 text-sm text-textPrimary">
        <p class="leading-snug">
          De <strong>{{ startDate }}</strong> às {{ startTime }}
        </p>
        <p class="leading-snug">
          Até <strong>{{ endDate }}</strong> às {{ endTime }}
        </p>
      </div>

      <div class="md:col-span-6 text-center">
        <div class="flex justify-around">
          <span class="text-md font-bold text-textPrimary">Passou</span>
          <span class="text-md font-bold text-textPrimary">Assumiu</span>
        </div>
        <div
          class="flex items-stretch justify-center gap-2 rounded-md bg-gray-200 px-3 py-2 md:items-center md:px-6 md:py-1 text-center"
        >
          <div
            class="flex flex-col items-start justify-center md:flex-row md:items-center md:gap-2 min-w-0 flex-1"
          >
            <span class="text-sm font-medium text-left md:truncate md:max-w-[150px]">{{
              personWhoPassedName
            }}</span>
            <span class="text-xs font-bold text-textPrimary whitespace-nowrap">{{
              personWhoPassedId
            }}</span>
          </div>
          <v-icon color="text-textPrimary" class="flex-shrink-0 self-center">{{
            mdiArrowRightThin
          }}</v-icon>
          <div
            class="flex flex-col items-start justify-center md:flex-row md:items-center md:gap-2 min-w-0 flex-1"
          >
            <span class="text-sm font-medium text-left md:truncate md:max-w-[150px]">{{
              personWhoAssumedName
            }}</span>
            <span class="text-xs font-bold text-textPrimary whitespace-nowrap">{{
              personWhoAssumedId
            }}</span>
          </div>
        </div>
      </div>

      <div class="md:col-span-2 text-sm text-textPrimary">
        <p class="font-bold text-gray-800">Escala</p>
        <p class="leading-snug text-textPrimary">{{ scaleName }}</p>
        <p class="leading-snug text-xs text-textPrimary">{{ location }}</p>
      </div>

      <div class="absolute top-3 right-3 md:static md:col-span-1 md:flex md:justify-end">
        <v-icon class="cursor-pointer" color="text-textPrimary" @click="toggleExpansion">
          {{ isExpanded ? mdiChevronUp : mdiChevronDown }}
        </v-icon>
      </div>
    </div>

    <div
      v-if="isExpanded"
      class="flex flex-col gap-3 md:flex-row md:justify-between border-t bg-gray-200 px-4 py-3 md:px-6 md:py-4 text-sm"
    >
      <div>
        <div class="text-textPrimary">
          <strong class="text-textPrimary">ID da troca:</strong> #{{ historyItem.id }}
        </div>
        <div class="text-textPrimary">
          <strong class="text-textPrimary">Data e hora da troca:</strong> {{ tradeDateTime }}
        </div>
      </div>

      <div>
        <div class="text-textPrimary">
          <strong class="text-textPrimary">Empresa:</strong> {{ companyName }}
        </div>
        <div class="text-textPrimary">
          <strong class="text-textPrimary">Posição:</strong> {{ positionName }}
        </div>
      </div>

      <div class="flex gap-2 md:gap-4">
        <OutlineButton
          text="Ver termos"
          @click="onViewTerms"
          height="32px"
          class="flex-1 md:flex-none bg-white"
        />
        <OutlineButton
          text="Ver plantão"
          @click="onViewShiftClick"
          height="32px"
          class="flex-1 md:flex-none bg-white"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'
import { mdiArrowRightThin, mdiChevronDown, mdiChevronUp } from '@mdi/js'
import OutlineButton from '../../../components/designSystem/Buttons/OutlineButton/index.vue'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { HandoverInviteResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import { translateTimezone } from '@/utils/timeConverter'

const props = defineProps<{
  historyItem: HandoverInviteResponse
}>()

const emit = defineEmits(['view-shift', 'view-terms'])

const isExpanded = ref(false)

const startDate = computed(() => formatDate(props.historyItem.doctorStation?.startHour))
const startTime = computed(() => formatTime(props.historyItem.doctorStation?.startHour))
const endDate = computed(() => formatDate(props.historyItem.doctorStation?.endHour))
const endTime = computed(() => formatTime(props.historyItem.doctorStation?.endHour))

const personWhoPassedName = computed(() => props.historyItem.doctorOrigin?.mainCrm?.name ?? 'N/D')
const personWhoPassedId = computed(() => {
  const number = props.historyItem.doctorOrigin?.mainCrm?.number ?? ''
  const uf = props.historyItem.doctorOrigin?.mainCrm?.uf ?? ''
  if (number && uf) return `${number}-${uf}`
  return `${number}${uf}`.trim()
})

const personWhoAssumedName = computed(() => props.historyItem.doctorDestiny?.mainCrm?.name ?? 'N/D')
const personWhoAssumedId = computed(() => {
  const number = props.historyItem.doctorDestiny?.mainCrm?.number ?? ''
  const uf = props.historyItem.doctorDestiny?.mainCrm?.uf ?? ''
  if (number && uf) return `${number}-${uf}`
  return `${number}${uf}`.trim()
})

const scaleName = computed(() => props.historyItem.doctorStation?.station?.schedule?.name ?? 'N/D')
const location = computed(
  () => props.historyItem.doctorStation?.station?.schedule?.healthUnit?.name ?? 'N/D'
)

const tradeDateTime = computed(() => formatDateTime(props.historyItem.updatedAt))
const companyName = computed(
  () => props.historyItem.doctorStation?.station?.schedule?.healthUnit?.company?.name ?? 'N/D'
)
const positionName = computed(
  () => props.historyItem.doctorStation?.station?.position?.name ?? 'N/D'
)

const toggleExpansion = () => {
  isExpanded.value = !isExpanded.value
}

function onViewShiftClick() {
  emit('view-shift', props.historyItem.id)
}

function onViewTerms() {
  emit('view-terms')
}

function formatDate(dateString?: string | null) {
  if (!dateString) return 'N/D'
  return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR })
}

function formatTime(dateString?: string | null) {
  if (!dateString) return '--:--'
  return format(parseISO(dateString), 'HH:mm', { locale: ptBR })
}

function formatDateTime(dateString?: string | null) {
  if (!dateString) return 'N/D'
  return (
    format(parseISO(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) +
    ' (' +
    translateTimezone(Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone) +
    ')'
  )
}
</script>
